import { memo } from 'react';
import { useFieldTheme } from 'framework/field/hooks/useFieldTheme';
import moment from 'moment';

import { useCalendarContext } from '../context/CalendarContext';
import { CalendarDayElement, CalendarWrapperElement } from '../elements';
import { CALENDAR_MODE } from '../enums';
import { useCalendarTheme } from '../hooks/useCalendarTheme';
import { CalendarDayProps } from '../types';

import cn from '../Calendar.module.scss';

export const CalendarDay = memo(
  ({ day, offset, date, onMouseEnter, onMouseLeave }: CalendarDayProps) => {
    const { month, dates, min_date, max_date, mode, onClickDay, theme } = useCalendarContext();
    const [since, to] = dates;

    const field_theme = useFieldTheme(theme?.field);
    const calendar_theme = useCalendarTheme(theme?.calendar);

    const is_day = month.clone().add(offset, 'months').date(day);
    const is_day_multiple = dates.some((d) => is_day.isSame(d));
    const is_min = min_date ? moment(is_day).isBefore(min_date) : false;
    const is_max = max_date ? moment(is_day).isAfter(max_date) : false;
    const is_today = moment().isSame(is_day, 'day');
    const dates_sorted = [since, date || since].sort((a, b) => a?.valueOf() - b?.valueOf());

    const is_hover = !!date && is_day.isBetween(dates_sorted[0], dates_sorted[1], 'day', '[]');
    const is_hover_since = !!date && is_day?.isSame(dates_sorted[0], 'day');
    const is_since = since?.isSame(is_day, 'day');
    const is_to = mode === CALENDAR_MODE.RANGE && to?.isSame(is_day, 'day');
    const is_hover_to = !!date && is_day?.isSame(dates_sorted[1], 'day');
    const is_between =
      mode === CALENDAR_MODE.RANGE && since && to && is_day.isBetween(since, to, 'day', '[]');

    return (
      <CalendarWrapperElement
        data-since={is_hover_since}
        data-to={is_hover_to}
        data-hover={is_hover}
        data-between={is_between}
        theme={{ field: field_theme, calendar: calendar_theme }}
      >
        <CalendarDayElement
          key={`${day}-${offset}`}
          className={cn.day}
          data-disabled={is_min || is_max}
          data-boundary={offset !== 0}
          data-hover={is_hover}
          data-since={is_since}
          data-multiple={is_day_multiple}
          data-to={is_to}
          data-between={is_between}
          onMouseEnter={() => onMouseEnter(day, offset)}
          onMouseLeave={onMouseLeave}
          onClick={() => onClickDay(day, offset)}
          theme={{ field: field_theme, calendar: calendar_theme }}
        >
          {is_today && (
            <div className={cn.today} style={{ background: field_theme.color.normal }} />
          )}
          {day}
        </CalendarDayElement>
      </CalendarWrapperElement>
    );
  }
);
